// Geolocation on the device (D-91): one reading per capture point, high
// accuracy, ten seconds at most, the last known fix returned as cached
// when the reading does not arrive, `unavailable` when nothing is known.
// Never a continuous watch. The permission is asked once, at the day
// preparation, and its state kept in the session store.
//
// INC-15d (D1, point 191): a fix without a position says why — the
// permission refused, no position from the device or the signal, a reading
// that ran out of time with no last known fix, or no geolocation at all —
// so a visit that must have a position to close is kept open by the
// permission alone, and never by the device.
import type { GeoPoint, GeoPolicy } from '../api/types';
import type { FieldcoreDb, GeoPermissionState } from '../db/database';

export const GEO_TIMEOUT_MS = 10_000;

export type CaptureScope = 'visit' | 'values' | 'photos' | 'observations';

/** Why a fix carries no position, as the server stores it. */
export type GeoUnavailableReason = NonNullable<GeoPoint['unavailable_reason']>;

export interface GeoServiceOptions {
  /** The browser's geolocation, or null where there is none. */
  geolocation?: Geolocation | null;
  permissions?: Permissions | null;
  now?: () => number;
  timeoutMs?: number;
}

function unavailable(now: number, reason: GeoUnavailableReason): GeoPoint {
  return {
    latitude: null,
    longitude: null,
    accuracy_m: null,
    altitude_m: null,
    source: 'unavailable',
    captured_at: new Date(now).toISOString(),
    is_cached: false,
    unavailable_reason: reason,
  };
}

/** The browser's error, in the server's words: 1 is the permission refused,
 *  3 the reading out of time, anything else no position to give. */
function reasonOf(error: GeolocationPositionError | null | undefined): GeoUnavailableReason {
  switch (error?.code) {
    case 1:
      return 'permission_denied';
    case 3:
      return 'timeout';
    default:
      return 'position_unavailable';
  }
}

function pointOf(position: GeolocationPosition, now: number): GeoPoint {
  const { coords } = position;
  return {
    latitude: coords.latitude,
    longitude: coords.longitude,
    accuracy_m: Number.isFinite(coords.accuracy) ? coords.accuracy : null,
    altitude_m: coords.altitude,
    // The browser fuses whatever providers it has; it never says which.
    source: 'fused',
    captured_at: new Date(
      Number.isFinite(position.timestamp) ? position.timestamp : now,
    ).toISOString(),
    is_cached: false,
  };
}

function browserGeolocation(): Geolocation | null {
  if (typeof navigator === 'undefined') return null;
  const candidate: unknown = Reflect.get(navigator, 'geolocation');
  return candidate === undefined || candidate === null ? null : (candidate as Geolocation);
}

function browserPermissions(): Permissions | null {
  if (typeof navigator === 'undefined') return null;
  const candidate: unknown = Reflect.get(navigator, 'permissions');
  return candidate === undefined || candidate === null ? null : (candidate as Permissions);
}

/** The capture points the organization's policy names; `visit` is always in. */
export function captureScopeOf(
  policy: GeoPolicy | Record<string, unknown> | null | undefined,
): Set<CaptureScope> {
  const raw = (policy ?? {}) as Record<string, unknown>;
  const scope = new Set<CaptureScope>(['visit']);
  if (Array.isArray(raw.capture_scope)) {
    for (const entry of raw.capture_scope as unknown[]) {
      if (
        entry === 'values' ||
        entry === 'photos' ||
        entry === 'observations' ||
        entry === 'visit'
      ) {
        scope.add(entry);
      }
    }
  }
  return scope;
}

export class GeoService {
  private readonly geolocation: Geolocation | null;
  private readonly permissions: Permissions | null;
  private readonly now: () => number;
  private readonly timeoutMs: number;
  private lastKnown: GeoPoint | null = null;
  private readings = 0;

  constructor(options: GeoServiceOptions = {}) {
    this.geolocation =
      options.geolocation === undefined ? browserGeolocation() : options.geolocation;
    this.permissions =
      options.permissions === undefined ? browserPermissions() : options.permissions;
    this.now = options.now ?? (() => Date.now());
    this.timeoutMs = options.timeoutMs ?? GEO_TIMEOUT_MS;
  }

  /** How many readings were requested; for the tests that count them. */
  get readingCount(): number {
    return this.readings;
  }

  get lastKnownPoint(): GeoPoint | null {
    return this.lastKnown;
  }

  /** One reading, at most `timeoutMs`; the last known fix as cached when
   *  the reading fails or times out; unavailable when nothing is known. */
  capture(): Promise<GeoPoint> {
    const now = this.now();
    const geolocation = this.geolocation;
    if (geolocation === null) return Promise.resolve(this.fallback(now, 'unsupported'));
    this.readings += 1;
    return new Promise<GeoPoint>((resolve) => {
      let settled = false;
      const finish = (point: GeoPoint): void => {
        if (settled) return;
        settled = true;
        resolve(point);
      };
      try {
        geolocation.getCurrentPosition(
          (position) => {
            const point = pointOf(position, now);
            this.lastKnown = point;
            finish(point);
          },
          (error) => {
            finish(this.fallback(now, reasonOf(error)));
          },
          { enableHighAccuracy: true, timeout: this.timeoutMs, maximumAge: 0 },
        );
      } catch {
        finish(this.fallback(now, 'position_unavailable'));
      }
      // The browser's own timeout is honoured; this one guards a provider
      // that never calls back at all. A browser still asking the person for
      // the permission has not given it: the reading is the permission's
      // refusal then, and not the time's (INC-15d, D3).
      setTimeout(() => {
        if (settled) return;
        const asked = this.permissions === null ? Promise.resolve(null) : this.queryPermission();
        void asked.then((state) => {
          finish(
            this.fallback(
              this.now(),
              state === 'prompt' || state === 'denied' ? 'permission_denied' : 'timeout',
            ),
          );
        });
      }, this.timeoutMs + 500);
    });
  }

  private fallback(now: number, reason: GeoUnavailableReason): GeoPoint {
    if (this.lastKnown !== null) {
      return { ...this.lastKnown, is_cached: true };
    }
    return unavailable(now, reason);
  }

  /** Ask for the permission by taking one reading, and record the answer. */
  async requestPermission(database: FieldcoreDb): Promise<GeoPermissionState> {
    return (await this.captureAndRecord(database)).state;
  }

  /** One reading, which asks for the permission where nobody has answered it
   *  yet, and the permission as it stands after it, recorded in the session
   *  store. INC-15d (D3): the close of a visit that must have a position takes
   *  its reading this way, so the device screen says what the close found. */
  async captureAndRecord(
    database: FieldcoreDb,
  ): Promise<{ point: GeoPoint; state: GeoPermissionState }> {
    const requestedAt = this.now();
    let state: GeoPermissionState;
    let point: GeoPoint;
    if (this.geolocation === null) {
      state = 'unsupported';
      point = await this.capture();
    } else {
      point = await this.capture();
      state = point.source === 'unavailable' ? await this.queryPermission() : 'granted';
    }
    await database.session.put({ key: 'geo', state, requestedAt });
    return { point, state };
  }

  private async queryPermission(): Promise<GeoPermissionState> {
    if (this.geolocation === null) return 'unsupported';
    if (this.permissions === null) return 'denied';
    try {
      const status = await this.permissions.query({ name: 'geolocation' });
      if (status.state === 'granted') return 'granted';
      if (status.state === 'denied') return 'denied';
      return 'prompt';
    } catch {
      return 'denied';
    }
  }
}
